/**
 * Tela principal.
 *
 * Decide entre chat e sala de voz conforme o tipo do canal aberto. Enquanto o
 * gateway não entrega o estado inicial, mostra um esqueleto no lugar de uma
 * tela vazia: mudar de "nada" para "tudo" de uma vez parece falha de carga.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Menu, MessageSquare, WifiOff } from 'lucide-react'
import type { Message } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useAppStore } from '@/store/app'
import { usePreferencesStore } from '@/store/preferences'
import { useGatewayStatus } from '@/hooks/useGatewayStatus'
import { EmptyState, Spinner } from '@/components/ui'
import { GuildRail } from '@/components/layout/GuildRail'
import { ChannelSidebar } from '@/components/layout/ChannelSidebar'
import { ChatView } from '@/components/chat/ChatView'
import { MemberList } from '@/components/layout/MemberList'
import { VoiceRoom } from '@/components/voice/VoiceRoom'
import { MessageComposerContext, type ComposerHandle } from '@/components/chat/composer-context'

export function AppView() {
  const ready = useAppStore((s) => s.ready)
  const currentChannelId = useAppStore((s) => s.currentChannelId)
  const currentGuildId = useAppStore((s) => s.currentGuildId)
  const channel = useAppStore((s) => (s.currentChannelId ? s.channels[s.currentChannelId] : null))
  const guildCount = useAppStore((s) => Object.keys(s.guilds).length)

  const status = useGatewayStatus()
  const offline = status === 'reconnecting' || status === 'closed'

  /*
    A lista de membros acompanha o servidor, não o canal.

    Ela ficava dentro da tela de chat, então sumia em qualquer momento sem
    canal aberto: entrar num servidor novo, ou fechar o canal atual, deixava a
    tela sem nenhuma pista de quem mais está ali. Numa sala de voz ela continua
    fora, porque os participantes já aparecem em tamanho grande no meio.
  */
  const showMemberList = usePreferencesStore((s) => s.ui.showMemberList)
  const setUi = usePreferencesStore((s) => s.setUi)
  const mostrarMembros =
    showMemberList && guildCount > 0 && currentGuildId !== null && channel?.type !== 'voice'

  /*
    Numa tela estreita as três colunas não cabem lado a lado: só o trilho de
    servidores e a lista de canais já ocupam mais do que a largura inteira de um
    celular. Elas viram gavetas que deslizam por cima da conversa, e a conversa
    fica com a tela toda, que é o que a pessoa veio ler.

    A partir de `lg` nada disso existe: o layout volta a ser as três colunas
    fixas de sempre, sem gaveta e sem botão para abrir.
  */
  const [gaveta, setGaveta] = useState<'canais' | 'membros' | null>(null)

  // Escolher um canal fecha a gaveta: ela cobre justamente o que a pessoa
  // acabou de pedir para ver.
  useEffect(() => {
    setGaveta(null)
  }, [currentChannelId])

  useEffect(() => {
    if (!gaveta) return
    const aoPressionar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setGaveta(null)
    }
    document.addEventListener('keydown', aoPressionar)
    return () => document.removeEventListener('keydown', aoPressionar)
  }, [gaveta])

  /*
    O acesso ao campo de escrita fica aqui em cima porque quem precisa dele não
    está todo dentro do chat: o menu do botão direito sobre uma pessoa aparece
    tanto na lista da direita quanto na barra lateral, e as duas precisam
    alcançar o mesmo campo para inserir uma menção.
  */
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const composerRef = useRef<{ focus: () => void; insertText: (text: string) => void }>({
    focus: () => {},
    insertText: () => {},
  })

  const registerHandle = useCallback(
    (handle: { focus: () => void; insertText: (text: string) => void }) => {
      composerRef.current = handle
    },
    [],
  )

  const composer: ComposerHandle = useMemo(
    () => ({
      setReplyTo: (message) => {
        setReplyTo(message)
        composerRef.current.focus()
      },
      focus: () => composerRef.current.focus(),
      insertText: (text) => composerRef.current.insertText(text),
    }),
    [],
  )

  if (!ready) {
    return (
      <div className="flex h-dvh items-center justify-center bg-surface-base">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="size-8 text-brand" />
          <p className="text-sm text-content-secondary">Conectando</p>
        </div>
      </div>
    )
  }

  return (
    /*
      Coluna, não linha: o aviso de conexão ocupa uma faixa própria e empurra a
      aplicação para baixo. Antes ele era posicionado por cima, e cobria o nome
      do servidor e o cabeçalho do canal justamente quando a pessoa precisava
      deles para entender o que estava acontecendo.
    */
    <div className="flex h-dvh flex-col overflow-hidden">
      {offline ? (
        <div
          role="status"
          className={cn(
            'flex shrink-0 items-center justify-center gap-2',
            'bg-warning py-1.5 text-[13px] font-medium text-surface-deepest',
          )}
        >
          <WifiOff className="size-4" />
          {status === 'reconnecting' ? 'Reconectando ao servidor' : 'Sem conexão com o servidor'}
        </div>
      ) : null}

      <MessageComposerContext.Provider value={composer}>
        <div className="relative flex min-h-0 flex-1">
          {/* O escurecido só existe enquanto uma gaveta está aberta, e fechar
              tocando fora dela é o gesto esperado em tela de toque. */}
          {gaveta ? (
            <button
              type="button"
              aria-label="Fechar menu"
              onClick={() => setGaveta(null)}
              className="fixed inset-0 z-40 bg-black/55 lg:hidden"
            />
          ) : null}

          <div
            className={cn(
              'flex shrink-0',
              'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-50',
              'max-lg:transition-transform max-lg:duration-200 max-lg:ease-out',
              gaveta === 'canais' ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full',
            )}
          >
            <GuildRail />
            <ChannelSidebar />
          </div>

          {guildCount === 0 ? (
            <TelaVazia onAbrirMenu={() => setGaveta('canais')}>
              <EmptyState
                icon={<MessageSquare />}
                title="Você ainda não está em nenhum servidor"
                description="Crie o seu primeiro servidor pelo botão de mais na barra lateral, ou entre em um usando um link de convite."
              />
            </TelaVazia>
          ) : !currentChannelId || !channel ? (
            <TelaVazia onAbrirMenu={() => setGaveta('canais')}>
              <EmptyState
                icon={<MessageSquare />}
                title="Escolha um canal"
                description="Selecione um canal na barra lateral para começar."
              />
            </TelaVazia>
          ) : channel.type === 'voice' ? (
            <VoiceRoom channelId={channel.id} onAbrirMenu={() => setGaveta('canais')} />
          ) : (
            <ChatView
              channelId={channel.id}
              replyTo={replyTo}
              onClearReply={() => setReplyTo(null)}
              registerHandle={registerHandle}
              onAbrirMenu={() => setGaveta('canais')}
              onAbrirMembros={() => {
                // A gaveta desliza a coluna que já existe: se ela estiver
                // desligada nas preferências, não há o que aparecer.
                setUi({ showMemberList: true })
                setGaveta('membros')
              }}
            />
          )}

          {mostrarMembros ? (
            <div
              className={cn(
                'shrink-0',
                'max-lg:fixed max-lg:inset-y-0 max-lg:right-0 max-lg:z-50',
                'max-lg:transition-transform max-lg:duration-200 max-lg:ease-out',
                gaveta === 'membros' ? 'max-lg:translate-x-0' : 'max-lg:translate-x-full',
              )}
            >
              <MemberList guildId={currentGuildId} />
            </div>
          ) : null}
        </div>
      </MessageComposerContext.Provider>
    </div>
  )
}

/**
 * Tela sem conversa aberta.
 *
 * Precisa do botão de menu como qualquer outra: numa tela estreita a lista de
 * canais está escondida, e sem esse botão a pessoa fica presa numa tela que
 * manda ela escolher um canal sem oferecer onde.
 */
function TelaVazia({
  onAbrirMenu,
  children,
}: {
  onAbrirMenu: () => void
  children: React.ReactNode
}) {
  return (
    <div className="relative flex flex-1 items-center justify-center bg-surface-base">
      <button
        type="button"
        onClick={onAbrirMenu}
        aria-label="Abrir canais"
        className="absolute top-2 left-2 flex size-9 items-center justify-center rounded-md text-content-secondary transition-colors hover:bg-surface-hover hover:text-content-primary lg:hidden"
      >
        <Menu className="size-5" />
      </button>

      {children}
    </div>
  )
}
